// pages/faq/FAQPage.tsx
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { getFaqList, FAQListItemDto, FAQSearchParams } from '../../api/deskApi';

const CATEGORIES = [
    { code: '', label: '전체' },
    { code: 'MEMBER', label: '회원' },
    { code: 'PAYMENT', label: '충전' },
    { code: 'SERVICE', label: '서비스' },
    { code: 'ETC', label: '기타' },
];

const FAQPage = () => {
    const [faqItems, setFaqItems] = useState<FAQListItemDto[]>([]);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const [loading, setLoading] = useState(false);
    const [totalCount, setTotalCount] = useState(0);
    const [keyword, setKeyword] = useState('');
    const [inputValue, setInputValue] = useState('');
    const [activeCategory, setActiveCategory] = useState('');

    const observer = useRef<IntersectionObserver | null>(null);
    const loadMoreRef = useRef<HTMLButtonElement>(null);

    const fetchFAQList = useCallback(async (pageNum: number, reset: boolean = false, kw: string = keyword, cat: string = activeCategory) => {
        if (loading) return;

        setLoading(true);

        try {
            const params: FAQSearchParams = {
                page: pageNum,
                size: 10,
            };
            if (kw) params.keyword = kw;
            if (cat) params.category = cat;

            const response = await getFaqList(params);

            if (response.data.code === '00' && response.data.data) {
                const pageData = response.data.data;
                const newItems = pageData.rows || [];

                setFaqItems(prev => reset ? newItems : [...prev, ...newItems]);
                setTotalCount(pageData.totalSize);

                const totalPages = Math.ceil(pageData.totalSize / pageData.pageSize);
                setHasMore(pageNum < totalPages);
            }
        } catch (error) {
            console.error('FAQ 조회 실패:', error);
        } finally {
            setLoading(false);
        }
    }, [loading, keyword, activeCategory]);

    const handleSearch = () => {
        setKeyword(inputValue);
        setPage(1);
        fetchFAQList(1, true, inputValue, activeCategory);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') handleSearch();
    };

    const handleCategoryChange = (code: string) => {
        setActiveCategory(code);
        setPage(1);
        fetchFAQList(1, true, keyword, code);
    };

    const handleLoadMore = () => {
        if (!loading && hasMore) {
            const nextPage = page + 1;
            setPage(nextPage);
            fetchFAQList(nextPage);
        }
    };

    useEffect(() => {
        const options = {
            root: null,
            rootMargin: '100px',
            threshold: 0.1,
        };

        observer.current = new IntersectionObserver((entries) => {
            if (entries[0].isIntersecting && hasMore && !loading) {
                handleLoadMore();
            }
        }, options);

        if (loadMoreRef.current) {
            observer.current.observe(loadMoreRef.current);
        }

        return () => {
            if (observer.current) observer.current.disconnect();
        };
    }, [hasMore, loading, page]);

    useEffect(() => {
        fetchFAQList(1, true);
    }, []);

    const getCategoryLabel = (category: string) => {
        return CATEGORIES.find(c => c.code === category)?.label || category;
    };

    return (
        <>
            <main className="container no-bottom-nav type_gray">
                {/* 검색 */}
                <section>
                    <div className="inner-box">
                        <div className="search-bar" style={{ display: 'flex', alignItems: 'center', border: '1px solid #ddd', borderRadius: '4px', padding: '0 12px', background: '#fff' }}>
                            <input
                                type="text"
                                value={inputValue}
                                onChange={e => setInputValue(e.target.value)}
                                onKeyDown={handleKeyDown}
                                placeholder="검색어를 입력해주세요"
                                style={{ flex: 1, border: 'none', outline: 'none', height: '44px', fontSize: '14px' }}
                            />
                            <button type="button" onClick={handleSearch} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0' }}>
                                <i className="fa fa-search" style={{ fontSize: '18px', color: '#555' }}></i>
                            </button>
                        </div>
                    </div>
                </section>

                {/* 카테고리 탭 */}
                <section className="no-gap">
                    <div className="tab-menu" style={{ display: 'flex', borderBottom: '1px solid #ddd', background: '#fff' }}>
                        {CATEGORIES.map(cat => (
                            <button
                                key={cat.code}
                                type="button"
                                onClick={() => handleCategoryChange(cat.code)}
                                style={{
                                    flex: 1,
                                    padding: '12px 0',
                                    border: 'none',
                                    background: 'none',
                                    cursor: 'pointer',
                                    fontSize: '14px',
                                    color: activeCategory === cat.code ? '#e00' : '#555',
                                    borderBottom: activeCategory === cat.code ? '2px solid #e00' : '2px solid transparent',
                                    fontWeight: activeCategory === cat.code ? 'bold' : 'normal',
                                }}
                            >
                                {cat.label}
                            </button>
                        ))}
                    </div>
                </section>

                {/* 목록 */}
                <section className="no-gap">
                    {faqItems.length > 0 ? (
                        <ul className="board-list">
                            {faqItems.map((item) => (
                                <li key={item.seq}>
                                    <a
                                        href={`/faq/${item.seq}`}
                                        onClick={(e) => {
                                            e.preventDefault();
                                            window.location.href = `/faq/${item.seq}`;
                                        }}
                                    >
                                        <p className="txt">
                                            <span className="category">
                                                {getCategoryLabel(item.category)}
                                            </span>
                                            <span className="tit">{item.title}</span>
                                        </p>
                                        <span className="ico">
                                            <i className="fa fa-angle-right" aria-hidden="true"></i>
                                        </span>
                                    </a>
                                </li>
                            ))}
                        </ul>
                    ) : (
                        !loading && (
                            <div style={{ padding: '40px', textAlign: 'center', color: '#999' }}>
                                {keyword ? `"${keyword}"에 대한 검색 결과가 없습니다.` : '등록된 FAQ가 없습니다.'}
                            </div>
                        )
                    )}

                    {loading && (
                        <div style={{ padding: '20px', textAlign: 'center' }}>
                            <i className="fa fa-spinner fa-spin" style={{ fontSize: '24px' }}></i>
                        </div>
                    )}
                </section>

                {hasMore && !loading && faqItems.length > 0 && (
                    <section className="no-gap">
                        <div className="inner-box">
                            <button
                                type="button"
                                className="page-more-btn"
                                ref={loadMoreRef}
                                onClick={handleLoadMore}
                            >
                                더보기 <i className="fa-solid fa-chevron-down"></i>
                            </button>
                        </div>
                    </section>
                )}

                {!hasMore && faqItems.length > 0 && (
                    <div style={{ padding: '20px', textAlign: 'center', color: '#999', fontSize: '14px' }}>
                        전체 {totalCount}개의 항목을 모두 불러왔습니다.
                    </div>
                )}
            </main>
        </>
    );
};

export default FAQPage;
